import { Field, Form } from "react-final-form";
import "./create.css";
import { VideoSidebar, type CreateFormValues, type GameClipIntervalRequest } from "../../../../gamePage/types";
import { useVideoPlayer } from "../../../../gamePage/Contexts/contexts";
import { TimeInput } from "./TimeInput";
import { _ } from "i18n";
import { useGameStore } from "../../../store";
import { useTimeStore } from "../../../time-store";
import { useEditStore } from "../../../edit-store";

interface CreateInterfaceProps {
	edit?: boolean;
	type: "event" | "clip";
	onEventSubmit?: (note: string, timestamp: number, title: string, id?: number) => void;
	onClipSubmit?: (intervals: GameClipIntervalRequest[], name: string, description?: string) => void;
	onDelete?: (id: number) => void;
}

export function CreateInterface({ edit, type, onEventSubmit, onDelete, onClipSubmit }: CreateInterfaceProps) {
	const { setSidebar } = useGameStore();

	const { currentTime } = useTimeStore();

	const { editEvent } = useEditStore();
	const { playerRef } = useVideoPlayer();

	const initValues = edit
		? {
			name: editEvent?.details?.title,
			note: editEvent?.details?.note,
			start: editEvent?.videoTime ? editEvent.videoTime / 1000 : 0,
		}
		: {
			start: currentTime / 1000 || 0,
			end: currentTime / 1000 ? currentTime / 1000 + 15 : 15,
		};

	const handleSubmit = (values: CreateFormValues) => {
		if (type === "event" && onEventSubmit) {
			onEventSubmit(values.note || "", Number.parseInt(values.start), values.name, Number(editEvent?.details?.id));
		}

		if (type === "clip" && onClipSubmit) {
			onClipSubmit(
				[
					{
						start: Number.parseInt(values.start) * 1000,
						end: Number.parseInt(values.end) * 1000,
						events: [],
					},
				],
				values.name,
				values.note,
			);
		}

		setSidebar(VideoSidebar.EventList);
		playerRef?.current?.play();
	};

	const handleCancel = () => {
		setSidebar(VideoSidebar.EventList);
		playerRef?.current?.play();
	};

	const handleDelete = () => {
		if (onDelete) {
			onDelete(Number(editEvent?.details?.id));
		}
		setSidebar(VideoSidebar.EventList);
		playerRef?.current?.play();
	};

	const validate = (values: CreateFormValues) => {
		const errors: Partial<CreateFormValues> = {};
		if (!values.name?.trim()) {
			errors.name = _("app.player.profile.position.error");
		}
		if (values.start == null) {
			errors.start = _("app.player.profile.position.error");
		}
		if (values.end == null && type === "clip") {
			errors.end = _("app.player.profile.position.error");
		}
		if (values.start != null && values.end != null && values.end <= values.start && type === "clip") {
			errors.end = _("label.validation.endStartTime");
		}
		return errors;
	};

	return (
		<div className="createInterface-container">
			<Form
				onSubmit={handleSubmit}
				validate={validate}
				initialValues={initValues}
				keepDirtyOnReinitialize
				render={({ handleSubmit }) => (
					<form onSubmit={handleSubmit} className="createForm-container">
						<div>
							<Field name="name">
								{({ input, meta }) => (
									<div className="createForm-item">
										<div className="createFormItem-label">{_("gamePage.create.name")}</div>
										<input
											{...input}
											className="createForm-inputField"
											type="text"
											id="event-name"
											placeholder={_("gamePage.create.name.placeholder")}
										/>
										{meta.error && meta.touched && <span className="error">{meta.error}</span>}
									</div>
								)}
							</Field>

							<div className="createForm-item">
								<div className="createFormItem-label">{_("gamePage.create.note")}</div>
								<Field
									className="createForm-inputField"
									name="note"
									component="input"
									id="note"
									placeholder={_("gamePage.create.note.placeholder")}
								/>
							</div>

							<div className="trimSection-label">
								{type === "clip" && _("gamePage.create.setLength")}
							</div>
							<div className="trim-section">
								<Field name="start">
									{({ input, meta }) => (
										<div className="createForm-item">
											<div className="createFormItem-label">{_("gamePage.create.timestamp")}</div>
											<TimeInput value={input.value} onChange={input.onChange} />
											{meta.error && meta.touched && <span className="error">{meta.error}</span>}
										</div>
									)}
								</Field>
								{type === "clip" && (
									<Field name="end">
										{({ input, meta }) => (
											<div className="createForm-item">
												<div className="createFormItem-label">{_("gamePage.create.end")}</div>
												<TimeInput value={input.value} onChange={input.onChange} />
												{meta.error && meta.touched && <span className="error">{meta.error}</span>}
											</div>
										)}
									</Field>
								)}
							</div>
						</div>
						<div className="createFormButtons">
							<button type="button" className="createFormButton cancel" onClick={handleCancel}>
								{_("btn.cancel")}
							</button>
							<button type="submit" className="createFormButton create">
								{_("match.highlight.btn.submit")}
							</button>
							{edit && (
								<button type="button" className="createFormButton delete" onClick={handleDelete}>
									{_("label.btn.deleteEvent")}
								</button>
							)}
						</div>
					</form>
				)}
			/>
		</div>
	);
}
